import { useState } from 'react';
import { UserProgress } from '../services/storage';

const AVATARS = ['🦊', '🐼', '🐰', '🦄', '🐻', '🐨', '🦁', '🐯', '🦋', '🐸'];

interface WelcomePageProps {
  onCreateUser: (name: string, age: number, avatar: string) => void;
  onSelectUser: (userId: string) => void;
  existingUsers: UserProgress[];
}

export default function WelcomePage({ onCreateUser, onSelectUser, existingUsers }: WelcomePageProps) {
  const [mode, setMode] = useState<'select' | 'create'>('select');
  const [name, setName] = useState('');
  const [age, setAge] = useState(7);
  const [selectedAvatar, setSelectedAvatar] = useState('🦊');

  const handleCreate = () => {
    if (!name.trim()) {
      alert('请输入你的名字');
      return;
    }
    onCreateUser(name.trim(), age, selectedAvatar);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-bounce-in">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="text-7xl mb-4 animate-float">📚</div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">英语小助手</h1>
          <p className="text-gray-500">快乐学英语，轻松又有趣！</p>
        </div>

        {mode === 'select' && existingUsers.length > 0 ? (
          // Select existing user
          <div>
            <h2 className="text-xl font-bold text-gray-700 mb-4">选择你的账号</h2>
            <div className="space-y-3 mb-6">
              {existingUsers.map(user => (
                <button
                  key={user.id}
                  onClick={() => onSelectUser(user.id)}
                  className="w-full p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl flex items-center gap-4 hover:shadow-lg transition-all card-hover"
                >
                  <span className="text-4xl">{user.avatar}</span>
                  <div className="flex-1 text-left">
                    <p className="font-bold text-gray-800">{user.name}</p>
                    <p className="text-sm text-gray-500">
                      Lv.{user.level} · {user.points}分
                    </p>
                  </div>
                </button>
              ))}
            </div>

            <div className="border-t pt-4">
              <button
                onClick={() => setMode('create')}
                className="w-full py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold hover:shadow-lg transition-all btn-bounce"
              >
                创建新账号
              </button>
            </div>
          </div>
        ) : (
          // Create new user
          <div className="space-y-6">
            <h2 className="text-xl font-bold text-gray-700">创建你的角色</h2>

            {/* Avatar selection */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                选择你的头像
              </label>
              <div className="grid grid-cols-5 gap-2">
                {AVATARS.map(avatar => (
                  <button
                    key={avatar}
                    onClick={() => setSelectedAvatar(avatar)}
                    className={`text-4xl p-3 rounded-2xl transition-all ${
                      selectedAvatar === avatar
                        ? 'bg-primary scale-110 shadow-lg'
                        : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    {avatar}
                  </button>
                ))}
              </div>
            </div>

            {/* Name input */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                你的名字
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="请输入你的名字"
                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-primary focus:outline-none transition-colors"
                maxLength={20}
              />
            </div>

            {/* Age selection */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">
                你的年龄
              </label>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setAge(Math.max(3, age - 1))}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 text-2xl font-bold transition-colors"
                >
                  -
                </button>
                <span className="text-3xl font-bold text-primary min-w-[60px] text-center">
                  {age}岁
                </span>
                <button
                  onClick={() => setAge(Math.min(15, age + 1))}
                  className="w-12 h-12 rounded-full bg-gray-100 hover:bg-gray-200 text-2xl font-bold transition-colors"
                >
                  +
                </button>
              </div>
            </div>

            {/* Submit buttons */}
            <div className="space-y-3 pt-4">
              <button
                onClick={handleCreate}
                className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:shadow-lg transition-all btn-bounce"
              >
                开始学习！🎉
              </button>

              {existingUsers.length > 0 && (
                <button
                  onClick={() => setMode('select')}
                  className="w-full py-3 bg-gray-100 text-gray-600 rounded-2xl font-medium hover:bg-gray-200 transition-all"
                >
                  返回选择账号
                </button>
              )}
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-8 pt-6 border-t">
          <p className="text-center text-sm text-gray-400">
            ✨ 完全免费 · 🎮 游戏化学习 · 🔊 标准发音
          </p>
        </div>
      </div>
    </div>
  );
}